import React from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import toast from '../shared/Toast';
import { productAction } from '../../_actions/product.action';
import { newServicesAction } from '../../_actions';
import { stocktakeService } from '../../_services';

/**
 * A reusable delete confirmation popup component
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether to show the popup
 * @param {Function} props.onHide - Function to call when hiding the popup
 * @param {string} props.itemType - Type of item being deleted ('product', 'service', 'productCategory', 'serviceCategory', 'brand', 'stocktake')
 * @param {string} props.itemId - ID of the item being deleted
 * @param {string} props.itemName - Name of the item being deleted
 * @param {Function} props.onSuccess - Function to call after successful deletion
 * @param {boolean} props.notAssigned - For services, whether the service is not assigned (affects UI)
 * @param {Function} props.refreshList - Function to refresh the list after deletion
 * @param {Function} props.refreshCategories - Function to refresh categories after deletion (for categories)
 */
function DeleteConfirmationPopup({
  show,
  onHide,
  itemType,
  itemId,
  itemName,
  onSuccess,
  notAssigned = true,
  refreshList,
  refreshCategories
}) {
  const dispatch = useDispatch();

  // Generate heading and description based on item type
  const getHeadingAndDescription = () => {
    switch (itemType) {
      case 'product':
        return {
          heading: `Delete Product - ${itemName}`,
          description: "Are you sure you want to delete this product? This action cannot be undone.",
          headingAssigned: null,
          descriptionAssigned: null
        };
      case 'service':
        return {
          heading: `Delete Service - ${itemName}`,
          description: "Are you sure you want to delete this service? This will delete it for all other staff using this service too. This action cannot be undone.",
          headingAssigned: 'This service is currently in use, would you like to disable it instead?',
          descriptionAssigned: 'This service is currently in use by other staff members so cannot be deleted. Would you like to disable this service instead?'
        };
      case 'productCategory':
        return {
          heading: `Delete Category - ${itemName}`,
          description: "Are you sure you want to delete this category? This will also delete all products within this category. This action cannot be undone.",
          headingAssigned: null,
          descriptionAssigned: null
        };
      case 'serviceCategory':
        return {
          heading: `Delete Category - ${itemName}`,
          description: "Are you sure you want to delete this category? This will also delete all services within this category for all staff using this category and these services. This action cannot be undone.",
          headingAssigned: null,
          descriptionAssigned: null
        };
      case 'brand':
        return {
          heading: `Delete Brand - ${itemName}`,
          description: "This action cannot be undone and products attached will lose their association.",
          headingAssigned: null,
          descriptionAssigned: null
        };
      case 'stocktake':
        return {
          heading: `Delete Stock Take`,
          description: "Are you sure you want to delete this stock take? This action cannot be undone.",
          headingAssigned: null,
          descriptionAssigned: null
        };
      default:
        return {
          heading: `Delete ${itemName}`,
          description: "Are you sure you want to delete this item? This action cannot be undone.",
          headingAssigned: null,
          descriptionAssigned: null
        };
    }
  };

  const { heading, description, headingAssigned, descriptionAssigned } = getHeadingAndDescription();

  // Handle delete action
  const handleDelete = () => {
    switch (itemType) {
      case 'product':
        deleteProduct();
        break;
      case 'service':
        deleteService();
        break;
      case 'productCategory':
        deleteProductCategory();
        break;
      case 'serviceCategory':
        deleteServiceCategory();
        break;
      case 'brand':
        deleteBrand();
        break;
      case 'stocktake':
        deleteStocktake();
        break;
      default:
        console.error(`Unknown item type: ${itemType}`);
    }
  };

  // Handle disable service (when service is in use)
  const handleDisable = () => {
    if (itemType === 'service') {
      const payload = {
        "commonServiceId": itemId,
      };
      
      dispatch(newServicesAction.serviceStatusChange(payload))
        .then((res) => {
          if(res?.serviceStatusChange?.statusCode === 1) {
            toast.success(res?.serviceStatusChange?.responseData?.message); 
            onHide();
          }
          
          if (refreshList) {
            refreshList();
          }
        })
        .catch((error) => {
          console.error("Error service StatusChange:", error);
        });
    }
  };

  // Delete product
  const deleteProduct = () => {
    const payload = {
      "productId": itemId,
    };
    
    dispatch(productAction.deleteProduct(payload))
      .then((res) => {
        if(res?.data?.statusCode === 1) {
          toast.success(res?.data?.responseData?.message || "Product deleted successfully");
          onHide();
          
          if (refreshList) {
            refreshList();
          }
        } else {
          toast.error(res?.data?.error?.responseMessage || "Failed to delete product");
        }
      })
      .catch((error) => {
        console.error("Error deleting product:", error);
        toast.error("Failed to delete product");
      });
  };

  // Delete service
  const deleteService = () => {
    const payload = {
      "commonServiceId": itemId,
    };
    
    dispatch(newServicesAction.deleteServicesData(payload))
      .then((res) => {
        if(res?.deleteServices?.statusCode === 1) {
          toast.success(res?.deleteServices?.responseData?.message);
          onHide();
        } else {
          toast.error(res?.deleteServices?.error ? 
            res?.deleteServices?.error?.responseMessage : 
            res?.deleteServices?.responseData?.message);
        }
        
        if (refreshList) {
          refreshList();
        }
      })
      .catch((error) => {
        console.error("Error delete service:", error);
      });
  };

  // Delete product category
  const deleteProductCategory = () => {
    const payload = {
      "categoryId": itemId,
    };
    
    dispatch(productAction.deleteProductCategory(payload))
      .then((res) => {
        if(res?.data?.statusCode === 1) {
          toast.success(res?.data?.responseData?.message || "Category deleted successfully");
          onHide();
          
          if (refreshList) {
            refreshList();
          }
          
          if (refreshCategories) {
            refreshCategories();
          }
        } else {
          toast.error(res?.data?.error?.responseMessage || "Failed to delete category");
        }
      })
      .catch((error) => {
        console.error("Error deleting product category:", error);
        toast.error("Failed to delete category");
      });
  };

  // Delete service category
  const deleteServiceCategory = () => {
    const payload = {
      "categoryId": itemId,
    };
    
    dispatch(newServicesAction.deleteCategoryData(payload))
      .then((res) => {
        if(res?.deleteCategory?.statusCode === 1) {
          toast.success(res?.deleteCategory?.responseData?.message);
          onHide();
        } else {
          toast.error(res?.deleteCategory?.error ? 
            res?.deleteCategory?.error?.responseMessage : 
            res?.deleteCategory?.responseData?.message);
        }
        
        if (refreshList) {
          refreshList();
        }
      })
      .catch((error) => {
        console.error("Error delete service category:", error);
      });
  };

  // Delete brand
  const deleteBrand = () => {
    dispatch(productAction.deleteProductBrand(itemId))
      .then((res) => {
        if(res?.data?.statusCode === 1) {
          toast.success(res?.data?.responseData?.message || "Brand deleted successfully");
          onHide();
          
          if (refreshList) {
            refreshList();
          }
        } else {
          toast.error(res?.data?.error?.responseMessage || "Failed to delete brand");
        }
      })
      .catch((error) => {
        console.error("Error deleting brand:", error);
        toast.error("Failed to delete brand");
      });
  };

  const deleteStocktake = () => {
    stocktakeService.deleteStocktake(itemId)
      .then((res) => {
        if (res?.data?.statusCode === 1) {
          toast.success(res?.data?.responseData?.message || "Stock take deleted successfully");
          onHide();

          if (typeof onSuccess === 'function') {
            onSuccess();
          }
        } else {
          toast.error(res?.data?.error?.responseMessage || "Failed to delete stock take");
        }
      })
      .catch((error) => {
        console.error("Error deleting stock take:", error);
        toast.error("Failed to delete stock take");
      });
  };

  return (
    <Modal 
      centered
      show={show} 
      onHide={onHide} 
      backdropClassName="_light_bg"
      size="lg" 
      className='_service__popup _service_delete_popup _no_animation'
    >
      <Modal.Body>
        <div className='_delete_popup'>
          {notAssigned !== false ? (
            <>
              <h4>{heading}</h4>
              <p>{description}</p>
              <div className="filter-actions text-right">
                <button 
                  className="btn btn-outline-dark pl-4 pr-4" 
                  onClick={onHide}
                >
                  Cancel
                </button>
                <button
                  className={`btn btn-dark ml-4 pl-4 pr-4 ${itemType === 'service' ? "_deletebtn" : ""}`}
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </div>
            </>
          ) : (
            <>
              <h4>{headingAssigned}</h4>
              <p>{descriptionAssigned}</p>
              <div className="filter-actions text-right">
                <button 
                  className="btn btn-outline-dark pl-4 pr-4" 
                  onClick={onHide}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-dark ml-4 pl-4 pr-4 _disabled"
                  onClick={handleDisable}
                >
                  Disable
                </button>
              </div>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default DeleteConfirmationPopup;